import { useEffect, useState } from "react";
import {
  getBoundaryCatalogOrder,
  collectUniqueSubjectKeysFromDatasets,
} from "../utils/boundaryCatalog";

const CATALOG_CACHE_KEY = "all-boundaries";
const catalogBundleCache = new Map();
const inflightCatalogBundleRequests = new Map();
const boundaryPayloadCache = new Map();
const inflightBoundaryPayloadRequests = new Map();

const fetchBoundaryPayload = async (candidateBoundary) => {
  if (boundaryPayloadCache.has(candidateBoundary)) {
    return boundaryPayloadCache.get(candidateBoundary);
  }

  const inflightRequest = inflightBoundaryPayloadRequests.get(candidateBoundary);
  if (inflightRequest) return inflightRequest;

  const request = fetch(`/data/${candidateBoundary}.json`, { cache: "no-cache" })
    .then(async (response) => {
      if (!response.ok) return null;
      return response.json();
    })
    .catch(() => null)
    .then((payload) => {
      inflightBoundaryPayloadRequests.delete(candidateBoundary);
      if (payload) {
        boundaryPayloadCache.set(candidateBoundary, payload);
      }
      return payload;
    });

  inflightBoundaryPayloadRequests.set(candidateBoundary, request);
  return request;
};

const getCatalogCacheKey = (selectedBoundary, includeAllBoundaries) =>
  includeAllBoundaries ? CATALOG_CACHE_KEY : selectedBoundary || "m24";

const loadCatalogBundle = async ({
  selectedBoundary,
  includeAllBoundaries = false,
}) => {
  const cacheKey = getCatalogCacheKey(selectedBoundary, includeAllBoundaries);

  if (catalogBundleCache.has(cacheKey)) {
    return catalogBundleCache.get(cacheKey);
  }

  const inflightRequest = inflightCatalogBundleRequests.get(cacheKey);
  if (inflightRequest) return inflightRequest;

  const boundariesToFetch = getBoundaryCatalogOrder(
    selectedBoundary,
    includeAllBoundaries
  );

  const request = Promise.all(
    boundariesToFetch.map(async (candidateBoundary) => {
      const payload = await fetchBoundaryPayload(candidateBoundary);
      return [candidateBoundary, payload];
    })
  )
    .then((entries) => {
      const dataByBoundary = {};
      entries.forEach(([candidateBoundary, payload]) => {
        if (payload) dataByBoundary[candidateBoundary] = payload;
      });

      const bundle = {
        dataByBoundary,
        catalogKeys: collectUniqueSubjectKeysFromDatasets(dataByBoundary, boundariesToFetch),
      };

      const hasAllRequestedBoundaries = boundariesToFetch.every(
        (candidateBoundary) => dataByBoundary[candidateBoundary]
      );

      if (hasAllRequestedBoundaries) {
        catalogBundleCache.set(cacheKey, bundle);
      }
      inflightCatalogBundleRequests.delete(cacheKey);
      return bundle;
    })
    .catch((error) => {
      inflightCatalogBundleRequests.delete(cacheKey);
      throw error;
    });

  inflightCatalogBundleRequests.set(cacheKey, request);
  return request;
};

export default function useBoundaryCatalog(selectedBoundary, options = {}) {
  const includeAllBoundaries = options.includeAllBoundaries === true;
  const catalogBoundary = includeAllBoundaries ? null : selectedBoundary;
  const boundaryKey = getCatalogCacheKey(catalogBoundary, includeAllBoundaries);
  const [dataByBoundary, setDataByBoundary] = useState({});
  const [catalogKeys, setCatalogKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cachedBundle = catalogBundleCache.get(boundaryKey);
    if (cachedBundle) {
      setDataByBoundary(cachedBundle.dataByBoundary);
      setCatalogKeys(cachedBundle.catalogKeys);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setDataByBoundary({});
    setCatalogKeys([]);
    setIsLoading(true);

    loadCatalogBundle({ selectedBoundary: catalogBoundary, includeAllBoundaries })
      .then((bundle) => {
        if (cancelled) return;
        setDataByBoundary(bundle.dataByBoundary);
        setCatalogKeys(bundle.catalogKeys);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDataByBoundary({});
        setCatalogKeys([]);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [boundaryKey, catalogBoundary, includeAllBoundaries]);

  return { dataByBoundary, catalogKeys, isLoading };
}
